import { useEffect, useState } from 'react';
import { api } from '@/api';
import type { TaskWithDetails, TaskStatus } from '@ultisim/shared';
import { format } from 'date-fns';

const priorityColors = {
  low: 'bg-dark-500',
  medium: 'bg-primary-500',
  high: 'bg-amber-500',
  urgent: 'bg-red-500',
};

const statusLabels: Record<TaskStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  deleted: 'Deleted',
};

export default function TasksPage() {
  const [tasks, setTasks] = useState<TaskWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<TaskStatus | 'all'>('all');
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  useEffect(() => {
    loadTasks();
  }, [filter]);

  const loadTasks = async () => {
    setIsLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const data = await api.get<TaskWithDetails[]>('/tasks', params);
      setTasks(data);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const completeTask = async (taskId: string) => {
    try {
      await api.post(`/tasks/${taskId}/complete`);
      loadTasks();
    } catch (err) {
      console.error('Failed to complete task:', err);
    }
  };

  const deleteTask = async (taskId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      loadTasks();
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  };

  const addTask = async () => {
    if (!newTaskTitle.trim()) return;
    try {
      const currentUser = await api.get<{ id: string }>('/auth/me');
      await api.post('/tasks', {
        title: newTaskTitle,
        userId: currentUser.id,
        priority: 'medium',
      });
      setNewTaskTitle('');
      setIsAddingTask(false);
      loadTasks();
    } catch (err) {
      console.error('Failed to add task:', err);
    }
  };

  const getStatusCount = (status: TaskStatus) => {
    return tasks.filter((t) => t.status === status).length;
  };

  if (isLoading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Tasks</h1>
          <p className="text-dark-400 mt-1">Manage your tasks and to-dos</p>
        </div>
        <button
          onClick={() => setIsAddingTask(true)}
          className="btn-primary"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Task
        </button>
      </div>

      {/* Add task form */}
      {isAddingTask && (
        <div className="card animate-slide-down">
          <h3 className="text-lg font-medium text-white mb-4">New Task</h3>
          <div className="flex gap-4">
            <input
              type="text"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addTask()}
              placeholder="Task title..."
              className="input flex-1"
              autoFocus
            />
            <button onClick={addTask} className="btn-primary">Add</button>
            <button onClick={() => setIsAddingTask(false)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(['pending', 'in_progress', 'completed'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(filter === status ? 'all' : status)}
            className={`card text-center transition-all ${
              filter === status ? 'ring-2 ring-primary-500' : ''
            }`}
          >
            <p className="text-2xl font-bold text-white">{getStatusCount(status)}</p>
            <p className="text-sm text-dark-400">{statusLabels[status]}</p>
          </button>
        ))}
        <button
          onClick={() => setFilter('all')}
          className={`card text-center transition-all ${
            filter === 'all' ? 'ring-2 ring-primary-500' : ''
          }`}
        >
          <p className="text-2xl font-bold text-white">{tasks.length}</p>
          <p className="text-sm text-dark-400">All Tasks</p>
        </button>
      </div>

      {/* Task list */}
      <div className="card">
        {tasks.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-dark-800 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-dark-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No tasks</h3>
            <p className="text-dark-400">
              {filter !== 'all' ? `No ${statusLabels[filter as TaskStatus].toLowerCase()} tasks` : 'Add your first task to get started'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-dark-800">
            {tasks.map((task) => (
              <div
                key={task.id}
                className={`p-4 flex items-start gap-4 hover:bg-dark-800/50 transition-colors ${
                  task.status === 'completed' ? 'opacity-60' : ''
                }`}
              >
                <button
                  onClick={() => task.status !== 'completed' && completeTask(task.id)}
                  className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 transition-colors ${
                    task.status === 'completed'
                      ? 'bg-accent-500 border-accent-500'
                      : 'border-dark-500 hover:border-accent-500'
                  }`}
                >
                  {task.status === 'completed' && (
                    <svg className="w-full h-full text-white p-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${priorityColors[task.priority]}`}
                    />
                    <p className={`font-medium ${task.status === 'completed' ? 'line-through text-dark-500' : 'text-dark-100'}`}>
                      {task.title}
                    </p>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-sm text-dark-500">
                    {task.project && (
                      <span className="flex items-center gap-1">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: task.project.color }}
                        />
                        {task.project.name}
                      </span>
                    )}
                    {task.dueDate && (
                      <span className={new Date(task.dueDate) < new Date() && task.status !== 'completed' ? 'text-red-400' : ''}>
                        Due {format(new Date(task.dueDate), 'MMM d')}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => deleteTask(task.id)}
                  className="p-1 text-dark-500 hover:text-red-400 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
